import React, { useState, useEffect } from 'react';
import { X, AlertTriangle, Euro } from 'lucide-react';
import api from '../../../api.js';
import { getActiveOrg } from '../../../api/core.js';
import { useTranslation } from 'react-i18next';

function FineFormModal({ fine, vehicles, drivers, onClose, onSuccess, onMessage, defaultVehicleId }) {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    // Overwritten as soon as a vehicle is selected — see onChange below.
    org_id: getActiveOrg() || 'ORG-001',
    vehicle_id: '',
    antai_reference: '',
    infraction_date: new Date().toISOString().split('T')[0],
    infraction_time: '',
    infraction_type: 'other',
    location: '',
    fine_amount: '',
    reduced_amount: '',
    increased_amount: '',
    points_deducted: 0,
    notes: '',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  // Libellés résolus au rendu (t) : une constante de module figerait la langue.
  const infractionTypes = [
    { code: 'speed', points: '1-6' },
    { code: 'parking', points: '0' },
    { code: 'red_light', points: '4' },
    { code: 'mobile_phone', points: '3' },
    { code: 'seatbelt', points: '3' },
    { code: 'stop_sign', points: '4' },
    { code: 'priority', points: '4' },
    { code: 'dangerous_driving', points: '6' },
    { code: 'other', points: '0-6' },
  ];

  // Pre-fill vehicle when defaultVehicleId is provided (create mode only)
  useEffect(() => {
    if (defaultVehicleId && !fine && !formData.vehicle_id) {
      const vehicle = vehicles?.find(v => v.id === Number(defaultVehicleId));
      if (vehicle) {
        setFormData(prev => ({
          ...prev,
          vehicle_id: String(vehicle.id),
          org_id: vehicle.org_id || prev.org_id
        }));
      }
    }
  }, [defaultVehicleId, vehicles]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (fine) {
      setFormData({
        org_id: fine.org_id || 'ORG-001',
        vehicle_id: fine.vehicle_id || '',
        antai_reference: fine.antai_reference || '',
        infraction_date: fine.infraction_date || '',
        infraction_time: fine.infraction_time || '',
        infraction_type: fine.infraction_type || 'other',
        location: fine.location || '',
        fine_amount: fine.fine_amount || '',
        reduced_amount: fine.reduced_amount || '',
        increased_amount: fine.increased_amount || '',
        points_deducted: fine.points_deducted || 0,
        notes: fine.notes || '',
      });
    }
  }, [fine]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.vehicle_id) {
      onMessage?.({
        title: t('fines.messages.errorTitle'),
        message: t('fines.errors.vehicleRequired'),
        type: 'error',
      });
      return;
    }

    if (!formData.fine_amount) {
      onMessage?.({
        title: t('fines.messages.errorTitle'),
        message: t('fines.errors.amountRequired'),
        type: 'error',
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const dataToSubmit = {
        ...formData,
        vehicle_id: parseInt(formData.vehicle_id),
        fine_amount: parseFloat(formData.fine_amount),
        reduced_amount: formData.reduced_amount ? parseFloat(formData.reduced_amount) : null,
        increased_amount: formData.increased_amount ? parseFloat(formData.increased_amount) : null,
        points_deducted: parseInt(formData.points_deducted) || 0,
      };

      if (fine) {
        await api.trafficFine.update(fine.id, dataToSubmit);
      } else {
        await api.trafficFine.create(dataToSubmit);
      }

      onSuccess?.();
    } catch (error) {
      onMessage?.({
        title: t('fines.messages.errorTitle'),
        message: error.message || t('fines.errors.saveFailed'),
        type: 'error',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-red-500" />
            <h2 className="text-lg font-semibold">
              {fine ? t('fines.form.titleEdit') : t('fines.form.titleNew')}
            </h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {/* Vehicle Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('fines.form.vehicle')} *
            </label>
            <select
              name="vehicle_id"
              value={formData.vehicle_id}
              onChange={(e) => {
                const selected = vehicles?.find(v => v.id === Number(e.target.value));
                setFormData(prev => ({
                  ...prev,
                  vehicle_id: e.target.value,
                  org_id: selected?.org_id || prev.org_id,
                }));
              }}
              className="w-full border rounded-lg px-3 py-2"
              required
            >
              <option value="">{t('fines.form.vehiclePlaceholder')}</option>
              {vehicles.map((v) => (
                <option key={v.id} value={v.id}>
                  {v.license_plate} - {v.make} {v.model}
                </option>
              ))}
            </select>
          </div>

          {/* ANTAI Reference */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('fines.form.antaiReference')}
            </label>
            <input
              type="text"
              name="antai_reference"
              value={formData.antai_reference}
              onChange={handleChange}
              placeholder="ANT-2024-XXXXX"
              className="w-full border rounded-lg px-3 py-2"
            />
          </div>

          {/* Date and Time */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('fines.form.infractionDate')} *
              </label>
              <input
                type="date"
                name="infraction_date"
                value={formData.infraction_date}
                onChange={handleChange}
                className="w-full border rounded-lg px-3 py-2"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('fines.form.time')}
              </label>
              <input
                type="time"
                name="infraction_time"
                value={formData.infraction_time}
                onChange={handleChange}
                className="w-full border rounded-lg px-3 py-2"
              />
            </div>
          </div>

          {/* Infraction Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('fines.form.infractionType')}
            </label>
            <select
              name="infraction_type"
              value={formData.infraction_type}
              onChange={handleChange}
              className="w-full border rounded-lg px-3 py-2"
            >
              {infractionTypes.map((type) => (
                <option key={type.code} value={type.code}>
                  {t('fines.form.typeOption', {
                    label: t(`fines.infractionTypes.${type.code}`),
                    points: type.points,
                  })}
                </option>
              ))}
            </select>
          </div>

          {/* Location */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('fines.form.location')}
            </label>
            <input
              type="text"
              name="location"
              value={formData.location}
              onChange={handleChange}
              placeholder={t('fines.form.locationPlaceholder')}
              className="w-full border rounded-lg px-3 py-2"
            />
          </div>

          {/* Amounts */}
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('fines.form.amount')} *
              </label>
              <div className="relative">
                <Euro className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="number"
                  name="fine_amount"
                  value={formData.fine_amount}
                  onChange={handleChange}
                  placeholder="135"
                  className="w-full border rounded-lg pl-9 pr-3 py-2"
                  min="0"
                  step="0.01"
                  required
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('fines.form.reducedAmount')}
              </label>
              <div className="relative">
                <Euro className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="number"
                  name="reduced_amount"
                  value={formData.reduced_amount}
                  onChange={handleChange}
                  placeholder="90"
                  className="w-full border rounded-lg pl-9 pr-3 py-2"
                  min="0"
                  step="0.01"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('fines.form.increasedAmount')}
              </label>
              <div className="relative">
                <Euro className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="number"
                  name="increased_amount"
                  value={formData.increased_amount}
                  onChange={handleChange}
                  placeholder="375"
                  className="w-full border rounded-lg pl-9 pr-3 py-2"
                  min="0"
                  step="0.01"
                />
              </div>
            </div>
          </div>

          {/* Points */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('fines.form.points')}
            </label>
            <input
              type="number"
              name="points_deducted"
              value={formData.points_deducted}
              onChange={handleChange}
              className="w-full border rounded-lg px-3 py-2"
              min="0"
              max="6"
            />
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('fines.form.notes')}
            </label>
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              rows={3}
              className="w-full border rounded-lg px-3 py-2"
              placeholder={t('fines.form.notesPlaceholder')}
            />
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border rounded-lg hover:bg-gray-50"
              disabled={isSubmitting}
            >
              {t('common.actions.cancel')}
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              disabled={isSubmitting}
            >
              {isSubmitting ? t('common.states.saving') : fine ? t('common.actions.edit') : t('common.actions.create')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default FineFormModal;
